import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { CustomerTabs } from "../components/CustomerTabs";
import { CustomerLogo } from "../components/CustomerLogo";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { num, fmtDate } from "../lib/format";
import { Paperclip, Plus, MessageSquarePlus, FileText, CheckCircle2, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = [
  { v: "NOT_STARTED", label: "Not started", cls: "text-muted-foreground" },
  { v: "IN_PROGRESS", label: "In Progress", cls: "text-sky-500" },
  { v: "COMPLETED", label: "Completed", cls: "text-pos" },
  { v: "NA", label: "N/A", cls: "text-muted-foreground" },
];
const statusCls = (v) => (STATUS.find((s) => s.v === v) || STATUS[0]).cls;

function TaskRow({ task, onStatus, onDue, onNote, onUpload }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const done = task.status === "COMPLETED" || task.status === "NA";
  const notes = task.notes || [];
  const docs = task.documents || [];
  return (
    <div
      data-testid={`checklist-task-${task.task_GUID}`}
      className={`flex flex-wrap items-center gap-3 rounded-lg border border-l-4 bg-card px-3 py-2 ${done ? "border-l-pos/60" : "border-l-border"}`}
    >
      <select
        data-testid={`task-status-${task.task_GUID}`}
        value={task.status}
        onChange={(e) => onStatus(task.task_GUID, e.target.value)}
        className={`h-8 w-[8.25rem] shrink-0 rounded-md border border-border bg-background px-2 text-sm font-semibold ${statusCls(task.status)}`}
      >
        {STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
      </select>

      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" title={task.task_description}>{task.task_description}</div>
        {(task.completed_by || task.team_member) && (
          <div className="flex items-center gap-2 truncate text-xs text-muted-foreground">
            {task.completed_by ? (
              <span className="inline-flex items-center gap-1 text-pos"><CheckCircle2 size={11} /> {task.completed_by} · {fmtDate(task.completed_at)}</span>
            ) : null}
            {task.team_member ? <span>{task.team_member}</span> : null}
          </div>
        )}
      </div>

      <input
        type="date"
        data-testid={`task-due-${task.task_GUID}`}
        defaultValue={task.due_date ? String(task.due_date).slice(0, 10) : ""}
        onChange={(e) => onDue(task.task_GUID, e.target.value)}
        className="h-8 w-[8rem] shrink-0 rounded-md border border-border bg-background px-2 text-xs"
        title="Due date"
      />

      {/* Notes */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`notes-toggle-${task.task_GUID}`}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <MessageSquare size={14} /> Notes
            {notes.length ? <span className="rounded-full bg-foreground px-1.5 text-[11px] font-bold text-background">{notes.length}</span> : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="max-h-56 overflow-auto p-3">
            {notes.length ? (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li key={n.note_GUID} className="border-l-2 border-border pl-2 text-sm" data-testid={`task-note-${n.note_GUID}`}>
                    <div>{n.note_text}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">— {n.created_by} · {fmtDate(n.created_at)}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">No notes yet.</div>
            )}
          </div>
          <div className="flex items-center gap-2 border-t border-border p-2">
            <input
              data-testid={`note-input-${task.task_GUID}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note…"
              className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-foreground/40"
            />
            <Button size="sm" data-testid={`note-add-${task.task_GUID}`} disabled={!note.trim()}
              onClick={async () => { await onNote(task.task_GUID, note.trim()); setNote(""); }}>
              <MessageSquarePlus size={14} />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Files */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`files-toggle-${task.task_GUID}`}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <Paperclip size={14} /> Files
            {docs.length ? <span className="rounded-full bg-foreground px-1.5 text-[11px] font-bold text-background">{docs.length}</span> : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="max-h-56 overflow-auto p-3">
            {docs.length ? (
              <ul className="space-y-1.5">
                {docs.map((d) => (
                  <li key={d.doc_GUID}>
                    <a
                      href={`${API}/api/checklist-documents/${d.doc_GUID}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`task-doc-${d.doc_GUID}`}
                      className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm hover:border-foreground/40"
                    >
                      <FileText size={13} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{d.file_name}</span>
                      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">open</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">No files attached.</div>
            )}
          </div>
          <div className="border-t border-border p-2">
            <label
              data-testid={`doc-upload-${task.task_GUID}`}
              className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-2 text-sm hover:border-foreground/40 ${busy ? "pointer-events-none opacity-50" : ""}`}
            >
              <Paperclip size={14} /> {busy ? "Uploading…" : "Attach a file"}
              <input type="file" className="hidden" disabled={busy}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setBusy(true);
                  try { await onUpload(task.task_GUID, f); } finally { setBusy(false); e.target.value = ""; }
                }} />
            </label>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PayrollChecklist() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [runId, setRunId] = useState(null);
  const [newStep, setNewStep] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: client, isLoading: cl } = useQuery({
    queryKey: ["client", clientId],
    queryFn: async () => (await api.get(`/clients/${clientId}`)).data,
  });
  const runs = client?.runs || [];
  const activeRun = runId || runs[0]?.payroll_run_GUID;
  const key = ["checklist", activeRun];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/runs/${activeRun}/checklist`)).data,
    enabled: !!activeRun,
  });

  const recompute = (tasks) => tasks.filter((t) => t.status === "COMPLETED" || t.status === "NA").length;
  const mutateCache = (mapTask, extra) =>
    qc.setQueryData(key, (old) => {
      if (!old) return old;
      const tasks = old.tasks.map(mapTask);
      return { ...old, tasks, completed: recompute(tasks), ...(extra ? extra(tasks) : {}) };
    });
  const resync = () => qc.invalidateQueries({ queryKey: key });

  const onStatus = async (guid, status) => {
    const patch = status === "COMPLETED"
      ? { status, completed_at: new Date().toISOString(), completed_by: user?.email }
      : { status, completed_at: null, completed_by: null };
    mutateCache((t) => (t.task_GUID === guid ? { ...t, ...patch } : t)); // optimistic — instant
    try { await api.patch(`/checklist-tasks/${guid}`, { status }); }
    catch { toast.error("Save failed"); resync(); }
  };
  const onDue = async (guid, due_date) => {
    mutateCache((t) => (t.task_GUID === guid ? { ...t, due_date } : t));
    try { await api.patch(`/checklist-tasks/${guid}`, { due_date }); }
    catch { toast.error("Save failed"); resync(); }
  };
  const onNote = async (guid, text) => {
    try {
      const { data: n } = await api.post(`/checklist-tasks/${guid}/notes`, { note_text: text });
      mutateCache((t) => (t.task_GUID === guid ? { ...t, notes: [...(t.notes || []), n] } : t));
      toast.success("Note added");
    } catch { toast.error("Could not add note"); }
  };
  const onUpload = async (guid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: d } = await api.post(`/checklist-tasks/${guid}/documents`, fd);
      mutateCache((t) => (t.task_GUID === guid ? { ...t, documents: [...(t.documents || []), d] } : t));
      toast.success("File attached");
    } catch { toast.error("Upload failed"); }
  };
  const onAddStep = async () => {
    const desc = newStep.trim();
    if (!desc) return;
    setAdding(true);
    try {
      const { data: t } = await api.post(`/runs/${activeRun}/checklist/tasks`, { task_description: desc });
      qc.setQueryData(key, (old) => (old ? { ...old, tasks: [...old.tasks, t], total: old.total + 1 } : old));
      setNewStep("");
      toast.success("Step added (saved to template for future payrolls)");
    } catch { toast.error("Could not add step"); } finally { setAdding(false); }
  };

  if (cl)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  const c = client?.client || {};
  const total = data?.total || 0;
  const completed = data?.completed || 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <Layout>
      <Breadcrumbs items={[{ label: "Portfolio", to: "/" }, { label: c.client_name }]} />
      <div className="mt-4 flex items-center gap-4 border-b border-border pb-5">
        <CustomerLogo clientId={clientId} hasLogo={c.has_logo} size={56} iconSize={26} />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">{c.client_name}</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            {c.client_short_name}{c.provider_company_code ? ` · Co ${c.provider_company_code}` : ""}
          </p>
        </div>
      </div>

      <CustomerTabs clientId={clientId} active="checklist" />

      {runs.length === 0 ? (
        <div className="mt-8 rounded-md border border-border bg-card p-8 text-center text-muted-foreground">
          No payroll runs on file for this customer yet.
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <label className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-muted-foreground">Pay Register</span>
              <select
                data-testid="checklist-run-select"
                value={activeRun || ""}
                onChange={(e) => setRunId(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm font-semibold"
              >
                {runs.map((r) => (
                  <option key={r.payroll_run_GUID} value={r.payroll_run_GUID}>{r.run_label} — {fmtDate(r.pay_date)}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-3" data-testid="checklist-progress">
              <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-pos transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono text-sm font-bold">{num(completed)} / {num(total)}</span>
              <span className="text-xs text-muted-foreground">· {pct}% done</span>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            {isLoading ? (
              <Skeleton className="h-64 rounded-md" />
            ) : (
              (data?.tasks || []).map((t) => (
                <TaskRow key={t.task_GUID} task={t} onStatus={onStatus} onDue={onDue} onNote={onNote} onUpload={onUpload} />
              ))
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-card/40 p-3">
            <input
              data-testid="new-step-input"
              value={newStep}
              onChange={(e) => setNewStep(e.target.value)}
              placeholder="Add a new checklist step…"
              className="h-9 w-full min-w-[14rem] max-w-lg flex-1 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground/40"
            />
            <Button size="sm" data-testid="add-step-btn" disabled={!newStep.trim() || adding} onClick={onAddStep}>
              <Plus size={15} className="mr-1" /> Add step
            </Button>
          </div>
        </>
      )}
    </Layout>
  );
}
